import "./SpaceSettings.scss";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../Auth";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { DialogSection } from "App/common/Dialog";
import { useTheme } from "App/common/ThemeProvider";
import { Switch } from "App/core/Switch";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import {
  isSpacePrivateInboxEnabled,
  isValidUrl,
  PERMISSION,
  isSpacePrivateInboxToggleable,
  isSpaceAudioQualityLocked,
} from "@highnote/server/src/core/shared-util";
import {
  CONTROLLED_SHARE_FEATURES,
  ControlledFeatureTooltipLabel,
} from "App/core/ControlledFeatureCallout";
import { List, ListItemButton } from "App/common/List";
import { BUTTON_THEME, Button } from "App/core/Button";
import { highnote } from "@highnote/server/src/sdk";
import { useToast } from "App/common/useToast";
import { useSpaceRaw } from "../useEntities";
import { TextInput } from "App/common/TextInput";
import { debounce } from "lodash";
import moment from "moment";

// Default launch moment when the countdown is first switched on (the manager
// then adjusts it via the date input).
const DEFAULT_COUNTDOWN_OFFSET_DAYS = 7;

const FEATURES = [
  "privateInboxEnabled",
  "audioQualityLocked",
  "privateInboxRedirectUrl",
  "chatEnabled",
] as const;

type FeatureKey = (typeof FEATURES)[number];

export const SpaceSettings = () => {
  const { addErrorMessage } = useToast();
  const { user, isAllowed } = useAuth();
  const { entity: space, loading: isSpaceLoading } = useSpaceRaw();
  const { theme } = useTheme();

  const redirectInputRef = useRef<HTMLInputElement>(null);
  const [redirectInputValue, setRedirectInputValue] = useState<string>("");
  const [redirectInputInvalid, setRedirectInputInvalid] =
    useState<boolean>(false);

  // Space Countdown launch moment (epoch ms), or null = off. `countdownFrom` is
  // the optimistic saved value (drives the toggle); `draftCountdown` is the
  // date input's working value, committed via the "Save Changes" button.
  const [countdownFrom, setCountdownFrom] = useState<number | null>(
    space?.countdownFrom ?? null,
  );
  const [draftCountdown, setDraftCountdown] = useState<number | null>(
    space?.countdownFrom ?? null,
  );
  useEffect(() => {
    setDraftCountdown(countdownFrom);
  }, [countdownFrom]);

  const [isLockedOptimistic, setIsLockedOptimistic] = useState<
    Record<FeatureKey, boolean>
  >({
    privateInboxEnabled: Boolean(space?.privateInboxEnabled),
    audioQualityLocked: isSpaceAudioQualityLocked(space),
    privateInboxRedirectUrl: Boolean(space?.privateInboxRedirectUrl),
    chatEnabled: Boolean(space?.chatEnabled),
  });

  useEffect(() => {
    if (!isSpaceLoading && space) {
      setIsLockedOptimistic({
        privateInboxEnabled: space.privateInboxEnabled,
        audioQualityLocked: isSpaceAudioQualityLocked(space),
        privateInboxRedirectUrl: Boolean(space.privateInboxRedirectUrl),
        chatEnabled: space.chatEnabled,
      });

      setRedirectInputValue(space.privateInboxRedirectUrl || "");
      setCountdownFrom(space.countdownFrom ?? null);
    }
  }, [space, isSpaceLoading]);

  const updateRedirectUrl = useCallback(
    debounce(async (value: string) => {
      if (!isLockedOptimistic.privateInboxRedirectUrl) return;

      try {
        if (!isValidUrl(value)) {
          throw new Error("Invalid URL");
        }

        await highnote.updateSpace({
          id: space.id,
          data: {
            privateInboxRedirectUrl: value,
          },
        });
      } catch (err) {
        setRedirectInputInvalid(true);
        console.error("Error updating redirect url: ", err);
        addErrorMessage(`Could not update redirect URL: ${err.message}`);
      }
    }, 2000),
    [isLockedOptimistic.privateInboxRedirectUrl],
  );

  useEffect(() => {
    return () => updateRedirectUrl.cancel();
  }, [updateRedirectUrl]);

  const handleToggle = async (feature: FeatureKey, nowLocked: boolean) => {
    try {
      setIsLockedOptimistic({
        ...isLockedOptimistic,
        [feature]: nowLocked,
      });

      await highnote.updateSpace({
        id: space.id,
        data: {
          [feature]: nowLocked,
        },
      });
    } catch (err) {
      console.error("Error updating feature lock: ", err);
      setIsLockedOptimistic({
        ...isLockedOptimistic,
        [feature]: !nowLocked,
      });
      addErrorMessage(
        `Could not ${
          nowLocked ? "unlock" : "lock"
        } ${feature}. Please try again.`,
      );
    }
  };

  // Optimistically set + persist the countdown launch moment (null = off).
  const saveCountdownFrom = async (next: number | null) => {
    const prev = countdownFrom;
    setCountdownFrom(next);
    try {
      await highnote.updateSpace({
        id: space.id,
        data: { countdownFrom: next },
      });
    } catch (err) {
      setCountdownFrom(prev);
      console.error("Error updating countdown: ", err);
      addErrorMessage(
        "Could not update the launch countdown. Please try again.",
      );
    }
  };

  const isSpaceOwner = user && space && user.id === space.createdBy;
  const canManageSpace =
    Boolean(space) && isAllowed(PERMISSION.TO_MANAGE_SPACE, { space });

  const inCountdownCohort = true;
  // const inCountdownCohort = Boolean(user?.cohorts?.space_countdown);

  const canToggleAudioQualityLock =
    Boolean(space) && isAllowed(PERMISSION.TO_LOCK_AUDIO_QUALITY, { space });

  const isDisabled = {
    privateInbox: isSpaceLoading,
    privateInboxRedirectUrl: !canManageSpace || isSpaceLoading,
    audioQuality: !canToggleAudioQualityLock || isSpaceLoading,
    chatEnabled: !canManageSpace || isSpaceLoading,
    countdown: !canManageSpace || isSpaceLoading,
  };

  const isPrivateInboxEnabled = isSpacePrivateInboxEnabled(space);

  return (
    <ErrorBoundary name="SpaceSettings">
      <DialogSection
        className="highnote-space-editor-settings"
        data-theme={theme}
      >
        <List>
          {isSpacePrivateInboxToggleable(space) && (
            <ListItemButton
              className="highnote-space-editor-listbutton"
              disabled={isDisabled.privateInbox}
              onClick={() =>
                handleToggle(
                  "privateInboxEnabled",
                  !isLockedOptimistic.privateInboxEnabled,
                )
              }
            >
              <div
                data-disabled="false"
                className="highnote-space-editor-settings-inner"
              >
                <h3>Private Inbox</h3>
                <p>Accept files and collaborate with visitors privately</p>
              </div>

              <div>
                <Switch
                  disabled={isDisabled.privateInbox}
                  checked={isLockedOptimistic.privateInboxEnabled}
                />
              </div>
            </ListItemButton>
          )}
          {isPrivateInboxEnabled && isLockedOptimistic.privateInboxEnabled && (
            <ListItemButton
              className="highnote-space-editor-listbutton"
              disabled={isDisabled.privateInboxRedirectUrl}
              onClick={() =>
                handleToggle(
                  "privateInboxRedirectUrl",
                  !isLockedOptimistic.privateInboxRedirectUrl,
                )
              }
            >
              <section>
                <div
                  data-disabled="false"
                  className="highnote-space-editor-settings-inner"
                >
                  <h3>Redirect</h3>
                  <p>Redirect visitors to another page after they submit</p>
                </div>

                <PermissionTooltip
                  title={
                    canManageSpace ? "Contact the space owner." : undefined
                  }
                  hasPermission={canManageSpace}
                >
                  <div>
                    <Switch
                      disabled={isDisabled.privateInboxRedirectUrl}
                      checked={isLockedOptimistic.privateInboxRedirectUrl}
                    />
                  </div>
                </PermissionTooltip>
              </section>

              <TextInput
                placeholder="https://..."
                type="text"
                isSingleLine
                isDisabled={!isLockedOptimistic.privateInboxRedirectUrl}
                inputRef={redirectInputRef}
                value={redirectInputValue}
                onClick={(event) => event.stopPropagation()} // prevents clicking on the input and toggle redirectUrl switch
                onChange={(value) => {
                  setRedirectInputInvalid(false);
                  setRedirectInputValue(value);
                  updateRedirectUrl(value);
                }}
              />
              {isLockedOptimistic.privateInboxRedirectUrl &&
                redirectInputInvalid && (
                  <span className="input-error">Invalid URL</span>
                )}
            </ListItemButton>
          )}
          <ListItemButton
            className="highnote-space-editor-listbutton"
            disabled={isDisabled.audioQuality}
            onClick={() => {
              handleToggle(
                "audioQualityLocked",
                !isLockedOptimistic.audioQualityLocked,
              );
            }}
          >
            <div
              data-disabled={Boolean(!canToggleAudioQualityLock)}
              className="highnote-space-editor-settings-inner"
            >
              <h3>Audio Quality Lock</h3>
              <p>
                Lock audio at Original Quality in this space, regardless of
                listeners&apos; connection speed
              </p>
            </div>
            <PermissionTooltip
              title={
                isSpaceOwner
                  ? ControlledFeatureTooltipLabel[
                      CONTROLLED_SHARE_FEATURES.AUDIO_QUALITY_LOCK
                    ]
                  : canManageSpace
                    ? "Contact the space owner."
                    : undefined
              }
              hasPermission={canToggleAudioQualityLock}
            >
              <div>
                <Switch
                  className="highnote-space-editor-switch-audio-quality"
                  disabled={isDisabled.audioQuality}
                  checked={isLockedOptimistic.audioQualityLocked}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>

          <ListItemButton
            className="highnote-space-editor-listbutton"
            disabled={isDisabled.chatEnabled}
            onClick={() => {
              handleToggle("chatEnabled", !isLockedOptimistic.chatEnabled);
            }}
          >
            <div
              data-disabled={Boolean(!canManageSpace)}
              className="highnote-space-editor-settings-inner"
            >
              <h3>Chat</h3>
              <p>Enable chat in this space</p>
            </div>
            <PermissionTooltip
              title="Contact the space owner."
              hasPermission={canManageSpace}
            >
              <div>
                <Switch
                  disabled={isDisabled.chatEnabled}
                  checked={isLockedOptimistic.chatEnabled}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>

          {inCountdownCohort && (
            <ListItemButton
              className="highnote-space-editor-listbutton"
              disabled={isDisabled.countdown}
              onClick={() =>
                saveCountdownFrom(
                  countdownFrom == null
                    ? moment()
                        .add(DEFAULT_COUNTDOWN_OFFSET_DAYS, "days")
                        .valueOf()
                    : null,
                )
              }
            >
              <div
                data-disabled={Boolean(!canManageSpace)}
                className="highnote-space-editor-settings-inner"
              >
                <h3>Launch Countdown</h3>
                <p>
                  Show a countdown timer to people who haven&rsquo;t joined this
                  space yet
                </p>
              </div>
              <PermissionTooltip
                title="Contact the space owner."
                hasPermission={canManageSpace}
              >
                <div>
                  <Switch
                    disabled={isDisabled.countdown}
                    checked={countdownFrom != null}
                  />
                </div>
              </PermissionTooltip>
            </ListItemButton>
          )}
        </List>
      </DialogSection>

      {/* Revealed in its own padded section (mirrors the Submissions fields). */}
      {inCountdownCohort && countdownFrom != null && (
        <DialogSection
          data-theme={theme}
          className="highnote-space-editor-countdown-input"
        >
          <TextInput
            isSingleLine
            type="datetime-local"
            isDisabled={isDisabled.countdown}
            value={
              draftCountdown
                ? moment(draftCountdown).format("YYYY-MM-DDTHH:mm")
                : ""
            }
            onChange={(value) =>
              setDraftCountdown(value ? moment(value).valueOf() : null)
            }
          />
          {draftCountdown !== countdownFrom && (
            <Button
              theme={BUTTON_THEME.CTA}
              disabled={isDisabled.countdown || draftCountdown == null}
              onClick={() => saveCountdownFrom(draftCountdown)}
            >
              Save Changes
            </Button>
          )}
        </DialogSection>
      )}
    </ErrorBoundary>
  );
};
